import apiClient from './client';

/**
 * Test Kitchen feedback API.
 *
 * Findings land in DietechSoftwareMain (Postgres) rather than a facility or PM_Server database —
 * testers switch facilities mid-session and triage is cross-deployment.
 */

export const FEEDBACK_TAGS = ['BROKEN', 'MISSING', 'SLOWER', 'LOST', 'WRONG', 'BETTER'] as const;
export type FeedbackTag = (typeof FEEDBACK_TAGS)[number];

/**
 * Tag copy shown in the dialog. Written in the testers' own words rather than a technical
 * taxonomy — dietary staff pick these reliably, they do not pick "functional defect" reliably.
 */
export const FEEDBACK_TAG_INFO: Record<FeedbackTag, { label: string; hint: string }> = {
  BROKEN: { label: 'Broken', hint: 'It errored, froze, or spun forever' },
  MISSING: { label: 'Missing', hint: "WinForms does this, the website doesn't" },
  SLOWER: { label: 'Slower', hint: 'More clicks or more waiting than WinForms' },
  LOST: { label: "Couldn't find it", hint: 'I knew what I wanted and could not find where' },
  WRONG: { label: 'Wrong result', hint: 'It ran fine but the result is not right' },
  BETTER: { label: 'Better!', hint: 'This beats WinForms, worth protecting' },
};

export const FEEDBACK_SEVERITIES = [
  { value: 1, label: 'Stopped me', hint: 'Could not do the job at all' },
  { value: 2, label: 'Workaround', hint: 'Got there, but had to do something weird' },
  { value: 3, label: 'Annoying', hint: "Slowed me down, didn't stop me" },
  { value: 4, label: 'Cosmetic', hint: 'Looks off, work still gets done' },
] as const;

export const FEEDBACK_STATUSES = [
  'New',
  'Triaged',
  'Promoted',
  'Fixed',
  'WontFix',
  'Duplicate',
] as const;
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];

export interface FeedbackSubmission {
  tag: FeedbackTag;
  severity: number;
  summary: string;
  fKey_User?: number | null;
  user_Name?: string | null;
  fKey_Facility?: number | null;
  facility_Name?: string | null;
  connectionId?: number | null;
  route?: string | null;
  page_Title?: string | null;
  app_Version?: string | null;
  web_Version?: string | null;
  browser?: string | null;
  viewport?: string | null;
  context_Json?: string | null;
  screenshot_Base64?: string | null;
}

export interface FeedbackItem {
  pKey_Feedback: number;
  feedback_GUID: string;
  tag: FeedbackTag;
  severity: number;
  summary: string;
  fKey_User?: number | null;
  user_Name?: string | null;
  fKey_Facility?: number | null;
  facility_Name?: string | null;
  connectionId?: number | null;
  route?: string | null;
  page_Title?: string | null;
  app_Version?: string | null;
  web_Version?: string | null;
  browser?: string | null;
  viewport?: string | null;
  context_Json?: string | null;
  screenshot_Blob?: string | null;
  has_Screenshot: boolean;
  status: FeedbackStatus;
  status_Note?: string | null;
  gitHub_Issue_Number?: number | null;
  gitHub_Issue_Url?: string | null;
  created_DateTime: string;
  updated_DateTime?: string | null;
  updated_By?: string | null;
}

export interface FeedbackQuery {
  status?: string;
  tag?: string;
  maxSeverity?: number;
  fKey_User?: number;
  fKey_Facility?: number;
  search?: string;
  skip?: number;
  take?: number;
}

export interface FeedbackTesterStat {
  fKey_User?: number | null;
  user_Name?: string | null;
  count: number;
  last_Finding?: string | null;
  days_Silent?: number | null;
}

export interface FeedbackCountStat {
  key: string;
  count: number;
}

export interface FeedbackMetrics {
  total: number;
  open: number;
  wrongTagCount: number;
  wrongTagPercent: number;
  byTester: FeedbackTesterStat[];
  byTag: FeedbackCountStat[];
  bySeverity: FeedbackCountStat[];
  topRoutes: FeedbackCountStat[];
  routesVisited: number;
}

export async function submitFeedback(submission: FeedbackSubmission): Promise<FeedbackItem> {
  const { data } = await apiClient.post<FeedbackItem>('/api/Feedback', submission);
  return data;
}

export async function queryFeedback(query: FeedbackQuery = {}): Promise<FeedbackItem[]> {
  const { data } = await apiClient.get<FeedbackItem[]>('/api/Feedback', { params: query });
  return data;
}

export async function getFeedback(id: number): Promise<FeedbackItem> {
  const { data } = await apiClient.get<FeedbackItem>(`/api/Feedback/${id}`);
  return data;
}

export async function updateFeedbackStatus(
  id: number,
  status: FeedbackStatus,
  note?: string,
  updatedBy?: string,
): Promise<void> {
  await apiClient.patch(`/api/Feedback/${id}/status`, {
    status,
    status_Note: note ?? null,
    updated_By: updatedBy ?? null,
  });
}

export async function promoteFeedbackToGitHub(id: number): Promise<FeedbackItem> {
  const { data } = await apiClient.post<FeedbackItem>(`/api/Feedback/${id}/promote`);
  return data;
}

export async function getFeedbackMetrics(days = 60): Promise<FeedbackMetrics> {
  const { data } = await apiClient.get<FeedbackMetrics>('/api/Feedback/metrics', {
    params: { days },
  });
  return data;
}

/**
 * Records that a tester opened a screen.
 *
 * Fire-and-forget by design: coverage is telemetry, not a transaction, and a tester must never
 * see an error because a visit ping failed.
 */
export function recordPageVisit(userId: number, route: string): void {
  if (!userId || userId <= 0 || !route) return;
  void apiClient.post('/api/Feedback/visit', { fKey_User: userId, route }).catch(() => {
    /* coverage telemetry is best-effort */
  });
}

/** Authenticated screenshot URL. The blob container is private — never linked to directly. */
export function feedbackScreenshotUrl(id: number): string {
  const base = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_BASE_URL ?? '');
  return `${base}/api/Feedback/${id}/screenshot`;
}
